import type {
  HarmonyPackage,
  Iteration,
  TeamReferenceStory,
  TeamTag,
  User,
  Workitem,
  WorkitemTag,
  Workspace,
} from '@rhythmic/db/types'
import type { TeamType } from '@rhythmic/types'
import type { UseLiveQueryReturn } from '@tanstack/vue-db'
import { createHooks } from 'hookable'
import { computed, markRaw, ref, shallowRef, watch } from 'vue'
import type { TeamUser, WorkspaceUser } from '../../queries'
import {
  useHarmonyPlansForWorkspace,
  useIterationsByTeam,
  useReferenceStoriesByTeam,
  useTagsByTeam,
  useTeamsByWorkspaceSlug,
  useTeamUsers,
  useWorkitemsByTeamId,
  useWorkitemTagsByTeam,
  useWorkspaceBySlug,
  useWorkspaceUsers,
} from '../../queries'
import useTeamContextOnPlanIdChanged from './events/useTeamContext.onPlanIdChanged'
import useTeamContextOnTeamChanged from './events/useTeamContext.onTeamChanged'
import useTeamContextOnWorkspaceChanged from './events/useTeamContext.onWorkspaceChanged'
import useTeamContextOnWorkspaceLoaded from './events/useTeamContext.onWorkspaceLoaded'
import type { TeamContextEvents } from './useTeamContext.events.schema'

interface TeamContextLoggerLike {
  debug: (message: string, meta?: Record<string, unknown> | unknown) => void
}

interface InitTeamContextQueriesParams {
  logger: TeamContextLoggerLike
}

/**
 * Team context query orchestrator with explicit, hook-driven updates.
 *
 * Route params are synced into `workspaceSlug` / `teamSlug` / `harmonyPlanId` by
 * `team-context-router.client` and then propagated via `hookable` events.
 */
export default function useTeamContextQueries() {
  type TeamContextHookMap = {
    [K in keyof TeamContextEvents]: (event: TeamContextEvents[K]) => void | Promise<void>
  }

  const hooks = markRaw(createHooks<TeamContextHookMap>())

  const workspaceSlug = ref<string | undefined>(undefined)
  const teamSlug = ref<string | undefined>(undefined)
  const harmonyPlanId = ref<string | undefined>(undefined)

  const workspaceQuery = shallowRef<UseLiveQueryReturn<Workspace> | undefined>(undefined)
  const workspace = computed<Workspace | null>(() => workspaceQuery.value?.data.value[0] ?? null)
  const workspaceId = computed<string | undefined>(() => workspace.value?.id || undefined)
  const isLoadingWorkspace = computed<boolean>(() => workspaceQuery.value?.isLoading.value ?? false)

  const teamQuery = shallowRef<UseLiveQueryReturn<TeamType> | undefined>(undefined)
  const teams = computed<TeamType[] | undefined>(() => teamQuery.value?.data.value)
  const team = computed<TeamType | undefined>(() => teams.value?.find((t) => t.slug === teamSlug.value) || undefined)
  const isLoadingTeams = computed(() => teams.value === undefined)

  const teamTagsQuery = shallowRef<UseLiveQueryReturn<TeamTag> | undefined>(undefined)
  const tags = computed<TeamTag[]>(() => teamTagsQuery.value?.data.value ?? [])

  const workitemTagsQuery = shallowRef<UseLiveQueryReturn<WorkitemTag> | undefined>(undefined)
  const workitemTags = computed<WorkitemTag[]>(() => workitemTagsQuery.value?.data.value ?? [])

  const iterationsQuery = shallowRef<UseLiveQueryReturn<Iteration> | undefined>(undefined)
  const iterations = computed<Iteration[] | undefined>(() => iterationsQuery.value?.data.value)

  const harmonyPlansQuery = shallowRef<UseLiveQueryReturn<HarmonyPackage> | undefined>(undefined)
  const harmonyPlans = computed<HarmonyPackage[]>(() => harmonyPlansQuery.value?.data.value ?? [])

  const harmonyPlan = computed<HarmonyPackage | null | undefined>(() => {
    const plans = harmonyPlans.value
    if (!plans || plans.length === 0) return null
    if (plans.length === 1) return plans[0]
    return plans.find((plan) => plan.id === (harmonyPlanId.value ?? null)) ?? plans[0]
  })

  const isLoadingPlans = computed(() => harmonyPlansQuery.value?.isLoading.value ?? false)

  const workitemsQuery = shallowRef<UseLiveQueryReturn<Workitem> | undefined>(undefined)
  const workitems = computed<Workitem[]>(() => workitemsQuery.value?.data.value ?? [])
  const isLoadingWorkitems = computed<boolean>(() => workitemsQuery.value?.isLoading.value ?? false)

  const workspaceMembersQuery = shallowRef<UseLiveQueryReturn<WorkspaceUser> | undefined>(undefined)
  const workspaceMembers = computed<WorkspaceUser[] | undefined>(() => workspaceMembersQuery.value?.data.value)

  const teamMembersQuery = shallowRef<UseLiveQueryReturn<TeamUser> | undefined>(undefined)
  const teamMembers = computed<TeamUser[] | undefined>(() => teamMembersQuery.value?.data.value)

  const teamReferenceStoryQuery = shallowRef<UseLiveQueryReturn<TeamReferenceStory> | undefined>(undefined)
  const teamReferenceStories = computed<TeamReferenceStory[] | undefined>(
    () => teamReferenceStoryQuery.value?.data.value,
  )

  const filteredTeamWorkspaceMembers = computed<User[] | undefined>(() => [])

  let hasInitialized = false

  function clearTeamScopedQueries() {
    harmonyPlansQuery.value = undefined
    workitemsQuery.value = undefined
    teamTagsQuery.value = undefined
    workitemTagsQuery.value = undefined
    iterationsQuery.value = undefined
    teamMembersQuery.value = undefined
    teamReferenceStoryQuery.value = undefined
  }

  function clearWorkspaceScopedQueries() {
    workspaceMembersQuery.value = undefined
    clearTeamScopedQueries()
  }

  function init({ logger }: InitTeamContextQueriesParams) {
    if (hasInitialized) return

    hasInitialized = true

    useTeamContextOnWorkspaceChanged({
      hooks,
      logger,
      workspaceQuery,
      teamQuery,
      clearWorkspaceScopedQueries,
      createWorkspaceQuery: (workspaceSlug) => useWorkspaceBySlug(workspaceSlug),
      createTeamsQuery: (workspaceSlug) => useTeamsByWorkspaceSlug(workspaceSlug),
    })

    useTeamContextOnWorkspaceLoaded({
      hooks,
      logger,
      workspaceMembersQuery,
      createWorkspaceMembersQuery: (workspaceId) => useWorkspaceUsers(workspaceId),
    })

    useTeamContextOnTeamChanged({
      hooks,
      logger,
      teamQuery,
      iterationsQuery,
      harmonyPlansQuery,
      workitemsQuery,
      teamTagsQuery,
      workitemTagsQuery,
      teamMembersQuery,
      teamReferenceStoryQuery,
      clearTeamScopedQueries,
      createIterationsQuery: (teamId) => useIterationsByTeam(teamId),
      createHarmonyPlansQuery: (workspaceId, teamId) => useHarmonyPlansForWorkspace(workspaceId, teamId),
      createWorkitemsQuery: (teamId) => useWorkitemsByTeamId(teamId),
      createTeamTagsQuery: (teamId) => useTagsByTeam(teamId),
      createWorkitemTagsQuery: (teamId) => useWorkitemTagsByTeam(teamId),
      createTeamMembersQuery: (teamId, workspaceId) => useTeamUsers(teamId, workspaceId),
      createTeamReferenceStoriesQuery: (teamId) => useReferenceStoriesByTeam(teamId),
    })

    useTeamContextOnPlanIdChanged({ hooks, logger })

    watch(
      workspaceId,
      (nextWorkspaceId, previousWorkspaceId) => {
        if (!nextWorkspaceId || nextWorkspaceId === previousWorkspaceId) return

        void emitWorkspaceLoaded({
          workspaceSlug: workspaceSlug.value,
          workspaceId: nextWorkspaceId,
          workspace: workspace.value,
        })
      },
      { immediate: true },
    )

    watch(
      () => `${team.value?.workspace_id ?? ''}:${team.value?.id ?? ''}`,
      () => {
        const currentTeam = team.value

        void emitTeamChanged({
          teamSlug: teamSlug.value,
          previousTeamSlug: teamSlug.value,
          teamId: currentTeam?.id,
          workspaceId: currentTeam?.workspace_id,
          team: currentTeam,
        })
      },
      { immediate: true },
    )
  }

  function onWorkspaceChanged(handler: TeamContextHookMap['WorkspaceChanged']) {
    return hooks.hook('WorkspaceChanged', handler)
  }

  function onWorkspaceLoaded(handler: TeamContextHookMap['WorkspaceLoaded']) {
    return hooks.hook('WorkspaceLoaded', handler)
  }

  function onTeamChanged(handler: TeamContextHookMap['TeamChanged']) {
    return hooks.hook('TeamChanged', handler)
  }

  function onPlanIdChanged(handler: TeamContextHookMap['PlanIdChanged']) {
    return hooks.hook('PlanIdChanged', handler)
  }

  async function emitWorkspaceChanged(event: TeamContextEvents['WorkspaceChanged']) {
    return hooks.callHook('WorkspaceChanged', event)
  }

  async function emitWorkspaceLoaded(event: TeamContextEvents['WorkspaceLoaded']) {
    return hooks.callHook('WorkspaceLoaded', event)
  }

  async function emitTeamChanged(event: TeamContextEvents['TeamChanged']) {
    return hooks.callHook('TeamChanged', event)
  }

  async function emitPlanIdChanged(event: TeamContextEvents['PlanIdChanged']) {
    return hooks.callHook('PlanIdChanged', event)
  }

  return {
    init,
    hooks,
    onWorkspaceChanged,
    onWorkspaceLoaded,
    onTeamChanged,
    onPlanIdChanged,
    emitWorkspaceChanged,
    emitWorkspaceLoaded,
    emitTeamChanged,
    emitPlanIdChanged,
    workspaceSlug,
    teamSlug,
    harmonyPlanId,
    workspaceQuery,
    workspace,
    workspaceId,
    isLoadingWorkspace,
    teamQuery,
    teams,
    team,
    isLoadingTeams,
    teamTagsQuery,
    tags,
    workitemTagsQuery,
    workitemTags,
    iterationsQuery,
    iterations,
    harmonyPlansQuery,
    harmonyPlans,
    harmonyPlan,
    isLoadingPlans,
    workitemsQuery,
    workitems,
    isLoadingWorkitems,
    workspaceMembersQuery,
    workspaceMembers,
    teamMembersQuery,
    teamMembers,
    teamReferenceStoryQuery,
    teamReferenceStories,
    filteredTeamWorkspaceMembers,
  }
}
